import { useState, useEffect } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Mail, Phone, MapPin, MessageCircle } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/brunch", label: "Brunch", id: "brunch" },
  { to: "/bottega", label: "Bottega", id: "bottega" },
  { to: "/eventi", label: "Eventi", id: "eventi" },
  { to: "/chi-siamo", label: "Chi Siamo", id: "chi-siamo" },
  { to: "/contatti", label: "Contatti", id: "contatti" },
];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-stone-200/60" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="font-display text-xl sm:text-2xl font-semibold text-forest tracking-tight" data-testid="logo-link">
          Brunch <span className="text-terracotta italic">&</span> Bottega
        </Link>
        <nav className="hidden lg:flex items-center gap-7" aria-label="Navigazione principale">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.id}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-terracotta ${
                  isActive ? "text-terracotta" : "text-stone-700"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/brunch"
            data-testid="nav-cta-prenota"
            className="bg-forest hover:bg-forest-dark text-cream text-sm font-medium px-5 py-2.5 rounded-full transition-colors shadow-sm"
          >
            Prenota il tuo brunch
          </Link>
        </nav>
        <button
          className="lg:hidden p-2 text-forest"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-stone-200/60 bg-cream px-4 py-4 flex flex-col gap-1" aria-label="Menu mobile" data-testid="mobile-menu">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.id}`}
              className={({ isActive }) =>
                `py-3 px-3 rounded-xl text-base font-medium transition-colors ${
                  isActive ? "bg-sand text-terracotta" : "text-stone-700 hover:bg-sand"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/brunch"
            data-testid="mobile-nav-cta-prenota"
            className="mt-2 bg-forest text-cream text-center font-medium px-5 py-3 rounded-full"
          >
            Prenota il tuo brunch
          </Link>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-forest-dark text-cream/90" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <p className="font-display text-2xl font-semibold text-cream">
            Brunch <span className="text-terracotta italic">&</span> Bottega
          </p>
          <p className="mt-3 text-sm leading-relaxed text-cream/70">
            Brunch a domicilio, eventi privati e cose buone fatte a mano.
            <br />A Rimini, con calma e con burro.
          </p>
        </div>
        <nav aria-label="Link del sito" className="flex flex-col gap-2 text-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-2">Il sito</p>
          {NAV.map((item) => (
            <Link key={item.to} to={item.to} className="hover:text-terracotta transition-colors w-fit" data-testid={`footer-nav-${item.id}`}>
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex flex-col gap-2 text-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-2">Contatti & Legale</p>
          <p className="flex items-center gap-2"><MapPin size={14} aria-hidden="true" /> Rimini, Italia</p>
          <a href="mailto:brunchbottega@gmail.com" className="flex items-center gap-2 hover:text-terracotta transition-colors w-fit" data-testid="footer-email-link"><Mail size={14} aria-hidden="true" /> brunchbottega@gmail.com</a>
          <a href="tel:+393392303993" className="flex items-center gap-2 hover:text-terracotta transition-colors w-fit" data-testid="footer-phone-link"><Phone size={14} aria-hidden="true" /> +39 339 230 3993</a>
          <a href="https://www.instagram.com/brunch_bottega" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-terracotta transition-colors w-fit" data-testid="footer-instagram-link"><Instagram size={14} aria-hidden="true" /> @brunch_bottega</a>
          <a href="https://wa.me/393392303993?text=Ciao!%20Vi%20contatto%20dal%20sito%20di%20Brunch%20%26%20Bottega." target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-terracotta transition-colors w-fit" data-testid="footer-whatsapp-link"><MessageCircle size={14} aria-hidden="true" /> Scrivici su WhatsApp</a>
          <div className="flex gap-4 mt-3">
            <Link to="/privacy-policy" className="underline underline-offset-4 hover:text-terracotta transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/cookie-policy" className="underline underline-offset-4 hover:text-terracotta transition-colors" data-testid="footer-cookie-link">Cookie Policy</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-cream/50 flex flex-col sm:flex-row gap-2 justify-between">
          <p>© 2026 Brunch & Bottega — [RAGIONE SOCIALE] · P.IVA [P.IVA]</p>
          <p>Nessun cookie di profilazione, nessun tracker di marketing.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <a
        href="https://wa.me/393392303993?text=Ciao!%20Vi%20contatto%20dal%20sito%20di%20Brunch%20%26%20Bottega."
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Scrivici su WhatsApp"
        data-testid="whatsapp-float-button"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-forest hover:bg-forest-dark text-cream pl-4 pr-5 py-3.5 rounded-full shadow-lg shadow-forest/30 transition-all hover:shadow-xl active:scale-95"
      >
        <MessageCircle size={22} aria-hidden="true" />
        <span className="text-sm font-semibold hidden sm:inline">Scrivici</span>
      </a>
    </div>
  );
}
